import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { PageHeader, StatusBadge, TypeBadge, fmtDate } from "../components/common";
import { SeverityBadge, SEVERITY } from "../components/SeverityBadge";
import { Card } from "../components/ui/card";
import {
  Buildings, Target, Scan, Bug, WarningOctagon, Pulse,
} from "@phosphor-icons/react";
import {
  ResponsiveContainer, ScatterChart, Scatter, XAxis, YAxis, ZAxis, Tooltip, CartesianGrid,
  BarChart, Bar, Cell,
} from "recharts";

const METRICS = [
  { key: "workspaces", label: "Workspaces", icon: Buildings },
  { key: "targets", label: "Targets", icon: Target },
  { key: "scans", label: "Total Scans", icon: Scan },
  { key: "findings", label: "Findings", icon: Bug },
  { key: "active_scans", label: "Active Scans", icon: Pulse },
];

export default function Dashboard() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  const load = () => api.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => {
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, []);

  if (!data) return <div className="text-slate-400">Loading console…</div>;

  const sevBars = ["critical", "high", "medium", "low", "info"].map((k) => ({
    name: SEVERITY[k].label,
    value: data.severity[k] || 0,
    color: SEVERITY[k].color,
  }));

  const scatter = data.risk_matrix.map((f, i) => ({
    x: (i % 10) + 1 + Math.random(),
    y: f.cvss,
    z: 100,
    color: SEVERITY[f.severity]?.color || "#3b82f6",
    title: f.title,
    host: f.host,
  }));

  return (
    <div className="fade-up">
      <PageHeader
        overline="Security Operations"
        title="Console Overview"
        subtitle="Live posture across all authorized workspaces. Engines execute server-side in Australia East."
        testid="dashboard-header"
      />

      {/* Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
        {METRICS.map((m) => (
          <Card key={m.key} data-testid={`metric-${m.key}`}
            className="bg-[#0f172a] border-[#1e293b] rounded-lg p-4 hover:-translate-y-0.5 hover:border-blue-500/40 transition-transform transition-colors duration-150">
            <div className="flex items-center justify-between">
              <span className="overline text-slate-500">{m.label}</span>
              <m.icon size={18} className="text-blue-500" weight="bold" />
            </div>
            <div className="font-head text-3xl font-bold text-white mt-2">{data.counts[m.key]}</div>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        {/* Risk matrix */}
        <Card className="lg:col-span-2 bg-[#0f172a] border-[#1e293b] rounded-lg p-5" data-testid="risk-matrix-card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-head text-lg font-semibold text-white">Risk Matrix</h2>
              <p className="text-xs text-slate-500">CVSS severity distribution across findings</p>
            </div>
            <WarningOctagon size={20} className="text-orange-500" weight="bold" />
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <ScatterChart margin={{ top: 10, right: 10, bottom: 10, left: -10 }}>
              <CartesianGrid stroke="#1e293b" />
              <XAxis type="number" dataKey="x" name="Exposure" tick={{ fill: "#64748b", fontSize: 11 }} stroke="#1e293b" />
              <YAxis type="number" dataKey="y" name="CVSS" domain={[0, 10]} tick={{ fill: "#64748b", fontSize: 11 }} stroke="#1e293b" />
              <ZAxis type="number" dataKey="z" range={[60, 200]} />
              <Tooltip
                cursor={{ strokeDasharray: "3 3", stroke: "#3b82f6" }}
                contentStyle={{ background: "#0b1220", border: "1px solid #1e293b", borderRadius: 8, color: "#fff", fontSize: 12 }}
                formatter={(v, n) => (n === "CVSS" ? [v, "CVSS"] : null)}
                labelFormatter={() => ""}
              />
              <Scatter data={scatter}>
                {scatter.map((e, i) => (
                  <Cell key={i} fill={e.color} />
                ))}
              </Scatter>
            </ScatterChart>
          </ResponsiveContainer>
        </Card>

        {/* Severity breakdown */}
        <Card className="bg-[#0f172a] border-[#1e293b] rounded-lg p-5" data-testid="severity-breakdown-card">
          <h2 className="font-head text-lg font-semibold text-white mb-1">Severity Breakdown</h2>
          <p className="text-xs text-slate-500 mb-4">All open findings</p>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={sevBars} layout="vertical" margin={{ left: 10, right: 20 }}>
              <XAxis type="number" hide />
              <YAxis type="category" dataKey="name" tick={{ fill: "#94a3b8", fontSize: 12 }} width={70} stroke="#1e293b" />
              <Tooltip cursor={{ fill: "#1e293b55" }}
                contentStyle={{ background: "#0b1220", border: "1px solid #1e293b", borderRadius: 8, color: "#fff", fontSize: 12 }} />
              <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                {sevBars.map((e, i) => <Cell key={i} fill={e.color} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Recent scans */}
        <Card className="bg-[#0f172a] border-[#1e293b] rounded-lg p-5" data-testid="recent-scans-card">
          <h2 className="font-head text-lg font-semibold text-white mb-4">Recent Scans</h2>
          <div className="space-y-2">
            {data.recent_scans.length === 0 && <div className="text-sm text-slate-500">No scans yet.</div>}
            {data.recent_scans.map((s) => (
              <div key={s.id} onClick={() => navigate(`/scans`)}
                className="flex items-center justify-between rounded-md border border-[#1e293b] px-3 py-2.5 hover:bg-slate-800/50 cursor-pointer transition-colors duration-150">
                <div className="flex items-center gap-3 min-w-0">
                  <TypeBadge type={s.type} />
                  <span className="text-sm text-slate-300 truncate">{fmtDate(s.created_at)}</span>
                </div>
                <StatusBadge status={s.status} />
              </div>
            ))}
          </div>
        </Card>

        {/* Top findings */}
        <Card className="bg-[#0f172a] border-[#1e293b] rounded-lg p-5" data-testid="top-findings-card">
          <h2 className="font-head text-lg font-semibold text-white mb-4">Priority Findings</h2>
          <div className="space-y-2">
            {data.recent_findings.length === 0 && <div className="text-sm text-slate-500">No findings yet.</div>}
            {data.recent_findings.map((f) => (
              <div key={f.id} onClick={() => navigate("/findings")}
                className="flex items-center justify-between gap-3 rounded-md border border-[#1e293b] px-3 py-2.5 hover:bg-slate-800/50 cursor-pointer transition-colors duration-150">
                <div className="min-w-0">
                  <div className="text-sm text-white truncate">{f.title}</div>
                  <div className="text-xs text-slate-500 font-mono">{f.cve} · {f.host}:{f.port}</div>
                </div>
                <SeverityBadge severity={f.severity} cvss={f.cvss} />
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
